import { type SxProps, type Theme, Tooltip, Typography } from '@mui/material';
import classNames from 'classnames';
import {
  useState,
  useEffect,
  type Dispatch,
  type SetStateAction,
  type ReactElement,
  useMemo,
} from 'react';

/**
 * Wraps text with Unicode isolate characters to preserve LTR text order
 * when used inside an RTL container (for left-side ellipsis truncation).
 */
const wrapTextForReverseTruncate = (text: string): string =>
  `\u2066${text}\u2069`;

const reverseTruncateSx: SxProps<Theme> = {
  direction: 'rtl',
  textAlign: 'left',
};

export const OverflowTooltip = ({
  text = '',
  onClick,
  renderContent,
  shouldCloseOnScroll,
  reverseDirection,
}: {
  text: string | undefined;
  onClick?: () => void;
  renderContent?: (
    text: string,

    setTextElementEl: Dispatch<SetStateAction<HTMLElement | null>>
  ) => ReactElement;
  shouldCloseOnScroll?: boolean;
  reverseDirection?: boolean;
}) => {
  const [textElementEl, setTextElementEl] = useState<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const isOverflowed = useMemo(() => {
    return (
      !!textElementEl && textElementEl.scrollWidth > textElementEl.clientWidth
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textElementEl, text]);

  useEffect(() => {
    if (!shouldCloseOnScroll) return;

    const handleScroll = () => {
      setIsOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [shouldCloseOnScroll]);

  return (
    <Tooltip
      title={isOverflowed ? text : undefined}
      open={shouldCloseOnScroll ? isOpen : undefined}
      onOpen={() => shouldCloseOnScroll && setIsOpen(true)}
      onClose={() => shouldCloseOnScroll && setIsOpen(false)}
    >
      {renderContent ? (
        renderContent(
          reverseDirection ? wrapTextForReverseTruncate(text) : text,
          setTextElementEl
        )
      ) : (
        <Typography
          onClick={onClick}
          ref={setTextElementEl}
          component='span'
          sx={reverseDirection ? reverseTruncateSx : {}}
          className={classNames('truncate', {
            'underline hover:cursor-pointer hover:text-(--mui-palette-text-secondary)':
              onClick,
          })}
        >
          {reverseDirection ? wrapTextForReverseTruncate(text) : text}
        </Typography>
      )}
    </Tooltip>
  );
};
